import type { RemoteCustomerRow } from '../../sync/core/customerPullDomain';
import type { CustomerAccountListQuery, CustomerAccountRow } from '../domain/customerTypes';
import { normalizeSearchText } from '../../shared/format/searchText';

/**
 * Opciones extra del filtro de snapshot.
 * `debtSinceByCustomerId`: customer_id → ISO del cargo más viejo impago
 * (referencia estable de mora; `updated_at` se pisa con ediciones de perfil).
 */
export type CustomerSnapshotFilterOptions = {
  debtSinceByCustomerId?: Map<string, string>;
};

function digitsOnly(value: string | null | undefined): string {
  return (value ?? '').replace(/\D+/g, '');
}

function matchesSearch(row: RemoteCustomerRow, search: string): boolean {
  const q = normalizeSearchText(search);
  if (!q) return true;
  if (
    normalizeSearchText(row.name).includes(q) ||
    normalizeSearchText(row.phone).includes(q) ||
    normalizeSearchText(row.cuit).includes(q) ||
    normalizeSearchText(row.email ?? '').includes(q)
  ) {
    return true;
  }
  // Teléfono/CUIT: el usuario pega "1654-7120" o "1654 7120" pero el dato se
  // guarda E.164 ("+5491116547120"). Comparar solo dígitos (>=3 para no
  // amplificar ruido con búsquedas de 1-2 dígitos).
  const digitsQuery = digitsOnly(search);
  if (digitsQuery.length >= 3) {
    return (
      digitsOnly(row.phone).includes(digitsQuery) ||
      digitsOnly(row.cuit).includes(digitsQuery)
    );
  }
  return false;
}

function matchesStatusFilter(
  row: RemoteCustomerRow,
  statusFilter: CustomerAccountListQuery['statusFilter'],
  debtSince?: string | null,
): boolean {
  const balance = Number(row.balance ?? 0);
  const blocked = Boolean(row.blocked);
  switch (statusFilter) {
    case 'has_debt':
      return balance > 0;
    case 'no_debt':
      return balance <= 0;
    case 'blocked':
      return blocked;
    case 'overdue': {
      if (balance <= 0) return false;
      const days = Number((row as any).payment_terms_days ?? 0);
      if (!days || days <= 0) return false;
      // Referencia estable: cargo más viejo impago; sin él, updated_at (legacy).
      const base = debtSince ?? (row as any).updated_at;
      if (!base) return false;
      const due = new Date(base);
      due.setDate(due.getDate() + days);
      return due.getTime() < Date.now();
    }
    default:
      return true;
  }
}

function compareCustomers(a: RemoteCustomerRow, b: RemoteCustomerRow, sortBy: CustomerAccountListQuery['sortBy']): number {
  switch (sortBy) {
    case 'name_desc':
      return normalizeSearchText(b.name).localeCompare(normalizeSearchText(a.name), 'es');
    case 'balance_desc':
      return Number(b.balance ?? 0) - Number(a.balance ?? 0);
    case 'balance_asc':
      return Number(a.balance ?? 0) - Number(b.balance ?? 0);
    case 'updated_desc': {
      const aTime = Date.parse(String(a.updated_at ?? ''));
      const bTime = Date.parse(String(b.updated_at ?? ''));
      if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
        return bTime - aTime;
      }
      return String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? ''), 'es');
    }
    case 'limit_desc':
      return Number(b.credit_limit ?? 0) - Number(a.credit_limit ?? 0);
    case 'limit_asc':
      return Number(a.credit_limit ?? 0) - Number(b.credit_limit ?? 0);
    case 'utilization_desc': {
      const aUtil = Number(a.credit_limit ?? 0) > 0 ? Number(a.balance ?? 0) / Number(a.credit_limit) : 0;
      const bUtil = Number(b.credit_limit ?? 0) > 0 ? Number(b.balance ?? 0) / Number(b.credit_limit) : 0;
      return bUtil - aUtil;
    }
    case 'utilization_asc': {
      const aUtil = Number(a.credit_limit ?? 0) > 0 ? Number(a.balance ?? 0) / Number(a.credit_limit) : 0;
      const bUtil = Number(b.credit_limit ?? 0) > 0 ? Number(b.balance ?? 0) / Number(b.credit_limit) : 0;
      return aUtil - bUtil;
    }
    case 'name_asc':
    default:
      return normalizeSearchText(a.name).localeCompare(normalizeSearchText(b.name), 'es');
  }
}

export function filterCustomerSnapshotRows(
  customers: RemoteCustomerRow[],
  query: CustomerAccountListQuery = {},
  options?: CustomerSnapshotFilterOptions
): RemoteCustomerRow[] {
  let rows = customers.filter((row) => Boolean(row?.id));
  const search = query.search?.trim();
  if (search) {
    const extraIds = new Set(query.searchExtraCustomerIds ?? []);
    rows = rows.filter((row) => matchesSearch(row, search) || extraIds.has(String(row.id)));
  }
  if (query.statusFilter && query.statusFilter !== 'all') {
    const debtSinceById = options?.debtSinceByCustomerId;
    rows = rows.filter((row) =>
      matchesStatusFilter(row, query.statusFilter, debtSinceById?.get(String(row.id)))
    );
  }
  if (query.utilizationFilter && query.utilizationFilter !== 'all') {
    rows = rows.filter((row) => {
      const balance = Number(row.balance ?? 0);
      const limit = Number(row.credit_limit ?? 0);
      const ratio = limit > 0 ? balance / limit : 0;
      switch (query.utilizationFilter) {
        case 'unused': return limit <= 0 || balance <= 0;
        case 'near_limit': return ratio > 0.8 && ratio <= 1;
        case 'over_limit': return limit > 0 && balance > limit;
        default: return true;
      }
    });
  }
  if (query.datePreset && query.datePreset !== 'all') {
    const now = new Date();
    let fromDate: Date;
    if (query.datePreset === 'today') {
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (query.datePreset === 'this_week') {
      const day = now.getDay();
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
    } else {
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    const fromIso = fromDate.toISOString();
    rows = rows.filter((row) => {
      const upd = row.updated_at;
      return upd != null && String(upd) >= fromIso;
    });
  }
  const sortBy = query.sortBy ?? 'name_asc';
  return [...rows].sort((a, b) => compareCustomers(a, b, sortBy));
}

export function countCustomerSnapshotRows(
  customers: RemoteCustomerRow[],
  query: CustomerAccountListQuery = {},
  options?: CustomerSnapshotFilterOptions
): number {
  return filterCustomerSnapshotRows(customers, query, options).length;
}

export function remoteCustomerToListRow(
  row: RemoteCustomerRow,
  debtSince?: string | null
): CustomerAccountRow {
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    phone: String(row.phone ?? ''),
    cuit: row.cuit ?? null,
    creditLimit: Number(row.credit_limit ?? 0),
    balance: Number(row.balance ?? 0),
    blocked: Boolean(row.blocked),
    updatedAt: row.updated_at ? String(row.updated_at) : null,
    debtSince: debtSince ?? null,
    portalToken: null,
    lastReminderAt: null,
    email: row.email ?? null,
    address: row.address ?? null,
    taxCategory: row.tax_category ?? null,
    notes: row.notes ?? null,
    paymentTermsDays: row.payment_terms_days != null ? Number(row.payment_terms_days) : null,
    priceListId: (row as { price_list_id?: string | null }).price_list_id ?? null,
    taxCondition: row.tax_condition ?? null,
    businessName: row.business_name ?? null,
  };
}

export function fetchCustomerAccountsFromSnapshot(
  customers: RemoteCustomerRow[],
  opts?: {
    limit?: number;
    offset?: number;
    query?: CustomerAccountListQuery;
    debtSinceByCustomerId?: Map<string, string>;
  }
): CustomerAccountRow[] {
  const offset = opts?.offset ?? 0;
  const filterOptions: CustomerSnapshotFilterOptions | undefined = opts?.debtSinceByCustomerId
    ? { debtSinceByCustomerId: opts.debtSinceByCustomerId }
    : undefined;
  const filtered = filterCustomerSnapshotRows(customers, opts?.query ?? {}, filterOptions);
  const sliced =
    opts?.limit != null ? filtered.slice(offset, offset + opts.limit) : filtered.slice(offset);
  return sliced.map((row) =>
    remoteCustomerToListRow(row, opts?.debtSinceByCustomerId?.get(String(row.id)))
  );
}

/** Web: overlay SQLite local sobre snapshot remoto (clientes recién creados / pending sync). */
export function mergeCustomerSnapshotWithLocal(
  snapshotCustomers: RemoteCustomerRow[],
  localCustomers: RemoteCustomerRow[]
): RemoteCustomerRow[] {
  const byId = new Map<string, RemoteCustomerRow>();
  for (const row of snapshotCustomers) {
    if (row?.id) byId.set(String(row.id), row);
  }
  for (const local of localCustomers) {
    if (!local?.id) continue;
    const id = String(local.id);
    const existing = byId.get(id);
    const localTs = Date.parse(String(local.updated_at ?? ''));
    const remoteTs = Date.parse(String(existing?.updated_at ?? ''));
    const localWins =
      !existing ||
      (Number.isFinite(localTs) && Number.isFinite(remoteTs) && localTs > remoteTs);
    if (localWins) {
      const merged = { ...local };
      if (Boolean(existing?.blocked) && !Boolean(local.blocked)) {
        merged.blocked = true;
      }
      byId.set(id, merged);
    }
  }
  return Array.from(byId.values());
}
